import React, { useState, useEffect } from 'react';

const STORAGE_KEY = 'veiassistent_devices';

const DEVICE_ICONS = {
  chrome: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="12" r="10"/>
      <circle cx="12" cy="12" r="4"/>
      <line x1="21.17" y1="8" x2="12" y2="8"/>
      <line x1="3.95" y1="6.06" x2="8.54" y2="14"/>
      <line x1="10.88" y1="21.94" x2="15.46" y2="14"/>
    </svg>
  ),
  firefox: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="12" r="10"/>
      <path d="M12 2a10 10 0 0 1 7.38 16.74C17.79 20.4 15.04 22 12 22"/>
      <path d="M12 2C6.48 2 2 6.48 2 12c0 3.05 1.37 5.78 3.53 7.63"/>
    </svg>
  ),
  safari: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="12" r="10"/>
      <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/>
    </svg>
  ),
  mobile: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
      <line x1="12" y1="18" x2="12.01" y2="18"/>
    </svg>
  ),
  desktop: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
      <line x1="8" y1="21" x2="16" y2="21"/>
      <line x1="12" y1="17" x2="12" y2="21"/>
    </svg>
  ),
};

const SIMULATED_SESSIONS = [
  {
    id: 'session-current',
    browser: 'Chrome',
    browserKey: 'chrome',
    os: 'Windows 11',
    deviceType: 'desktop',
    location: 'Oslo, Norge',
    ip: '84.202.xxx.xxx',
    loginTime: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
    isCurrent: true,
  },
  {
    id: 'session-2',
    browser: 'Safari',
    browserKey: 'safari',
    os: 'iOS 17',
    deviceType: 'mobile',
    location: 'Bergen, Norge',
    ip: '195.88.xxx.xxx',
    loginTime: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    isCurrent: false,
  },
  {
    id: 'session-3',
    browser: 'Firefox',
    browserKey: 'firefox',
    os: 'macOS 14',
    deviceType: 'desktop',
    location: 'Trondheim, Norge',
    ip: '158.39.xxx.xxx',
    loginTime: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    isCurrent: false,
  },
];

const formatRelativeTime = (isoString) => {
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return 'Akkurat nå';
  if (mins < 60) return `${mins} minutt${mins !== 1 ? 'er' : ''} siden`;
  if (hours < 24) return `${hours} time${hours !== 1 ? 'r' : ''} siden`;
  return `${days} dag${days !== 1 ? 'er' : ''} siden`;
};

const formatFullTime = (isoString) => {
  return new Date(isoString).toLocaleString('nb-NO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ConnectedDevices = () => {
  const [sessions, setSessions] = useState([]);
  const [loggingOutId, setLoggingOutId] = useState(null);
  const [loggedOutIds, setLoggedOutIds] = useState([]);
  const [showConfirm, setShowConfirm] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setSessions(parsed);
      } catch {
        setSessions(SIMULATED_SESSIONS);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(SIMULATED_SESSIONS));
      }
    } else {
      setSessions(SIMULATED_SESSIONS);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(SIMULATED_SESSIONS));
    }
  }, []);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const handleLogout = (sessionId) => {
    setShowConfirm(sessionId);
  };

  const confirmLogout = (sessionId) => {
    setShowConfirm(null);
    setLoggingOutId(sessionId);
    setTimeout(() => {
      const updated = sessions?.filter(s => s?.id !== sessionId);
      setSessions(updated);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      setLoggedOutIds(prev => [...prev, sessionId]);
      setLoggingOutId(null);
      showToast('Enheten ble logget ut');
    }, 800);
  };

  const handleLogoutAll = () => {
    setShowConfirm('all');
  };

  const confirmLogoutAll = () => {
    setShowConfirm(null);
    const currentSession = sessions?.find(s => s?.isCurrent);
    const remaining = currentSession ? [currentSession] : [];
    setSessions(remaining);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(remaining));
    showToast('Alle andre enheter ble logget ut');
  };

  const activeSessions = sessions?.filter(s => !loggedOutIds?.includes(s?.id));
  const otherSessions = activeSessions?.filter(s => !s?.isCurrent);

  const cardStyle = {
    background: 'var(--color-card)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    padding: '20px 24px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    position: 'relative',
    transition: 'border-color 200ms',
  };

  const labelStyle = {
    fontFamily: 'var(--font-caption)',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--color-muted-foreground)',
    marginBottom: '2px',
  };

  return (
    <div className="card">
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed',
          bottom: '32px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'var(--color-foreground)',
          color: 'var(--color-background)',
          padding: '12px 24px',
          borderRadius: 'var(--radius-sm)',
          fontFamily: 'var(--font-caption)',
          fontSize: '13px',
          fontWeight: 600,
          zIndex: 9999,
          boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          animation: 'slideUp 0.3s cubic-bezier(0.22,1,0.36,1)',
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-accent)" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
          {toast}
        </div>
      )}

      {/* Confirm Modal */}
      {showConfirm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9998,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px',
        }}>
          <div style={{
            background: 'var(--color-card)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            padding: '32px',
            maxWidth: '400px',
            width: '100%',
          }}>
            <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '18px', fontWeight: 700, color: 'var(--color-foreground)', marginBottom: '12px' }}>
              {showConfirm === 'all' ? 'Logg ut alle enheter?' : 'Logg ut enhet?'}
            </h3>
            <p style={{ fontFamily: 'var(--font-caption)', fontSize: '14px', color: 'var(--color-muted-foreground)', marginBottom: '24px', lineHeight: 1.6 }}>
              {showConfirm === 'all' ?'Alle andre enheter vil bli logget ut. Din nåværende økt forblir aktiv.' :'Denne enheten vil bli logget ut umiddelbart.'}
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowConfirm(null)}
                style={{
                  padding: '10px 20px',
                  background: 'transparent',
                  border: '2px solid var(--color-border)',
                  borderRadius: 'var(--radius-sm)',
                  fontFamily: 'var(--font-heading)',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: 'var(--color-muted-foreground)',
                  cursor: 'pointer',
                }}
              >
                Avbryt
              </button>
              <button
                onClick={() => showConfirm === 'all' ? confirmLogoutAll() : confirmLogout(showConfirm)}
                style={{
                  padding: '10px 20px',
                  background: '#dc2626',
                  border: '2px solid #dc2626',
                  borderRadius: 'var(--radius-sm)',
                  fontFamily: 'var(--font-heading)',
                  fontSize: '13px',
                  fontWeight: 700,
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                Logg ut
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground mb-2">
            Tilkoblede enheter
          </h2>
          <p style={{ fontFamily: 'var(--font-caption)', fontSize: '14px', color: 'var(--color-muted-foreground)' }}>
            {activeSessions?.length} aktiv{activeSessions?.length !== 1 ? 'e' : ''} økt{activeSessions?.length !== 1 ? 'er' : ''}
          </p>
        </div>
        {otherSessions?.length > 0 && (
          <button
            onClick={handleLogoutAll}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 18px',
              background: 'transparent',
              border: '2px solid #dc2626',
              borderRadius: 'var(--radius-sm)',
              fontFamily: 'var(--font-heading)',
              fontSize: '12px',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: '#dc2626',
              cursor: 'pointer',
              transition: 'background 150ms, color 150ms',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#dc2626'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#dc2626'; }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            Logg ut alle andre
          </button>
        )}
      </div>

      {/* Session list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {activeSessions?.map((session) => (
          <div
            key={session?.id}
            style={{
              ...cardStyle,
              borderColor: session?.isCurrent ? 'var(--color-accent)' : 'var(--color-border)',
              borderLeftWidth: session?.isCurrent ? '3px' : '1px',
            }}
          >
            {/* Device icon */}
            <div style={{
              width: '44px',
              height: '44px',
              borderRadius: 'var(--radius-sm)',
              background: session?.isCurrent ? 'rgba(255,87,34,0.1)' : 'rgba(0,79,152,0.08)',
              border: `2px solid ${session?.isCurrent ? 'rgba(255,87,34,0.25)' : 'rgba(0,79,152,0.15)'}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              color: session?.isCurrent ? 'var(--color-accent)' : 'var(--color-primary)',
            }}>
              {DEVICE_ICONS[session?.deviceType] || DEVICE_ICONS.desktop}
            </div>

            {/* Info */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'var(--font-heading)', fontSize: '15px', fontWeight: 700, color: 'var(--color-foreground)' }}>
                  {session?.browser} · {session?.os}
                </span>
                {session?.isCurrent && (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: '2px 10px',
                    background: 'rgba(255,87,34,0.12)',
                    border: '2px solid rgba(255,87,34,0.3)',
                    borderRadius: 'var(--radius-sm)',
                    fontFamily: 'var(--font-caption)',
                    fontSize: '10px',
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: 'var(--color-accent)',
                  }}>
                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--color-accent)', display: 'inline-block' }} />
                    Denne enheten
                  </span>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '10px 20px' }}>
                <div>
                  <p style={labelStyle}>Plassering</p>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: '13px', color: 'var(--color-foreground)' }}>{session?.location}</p>
                </div>
                <div>
                  <p style={labelStyle}>IP-adresse</p>
                  <p style={{ fontFamily: 'var(--font-data)', fontSize: '13px', color: 'var(--color-foreground)' }}>{session?.ip}</p>
                </div>
                <div>
                  <p style={labelStyle}>Innlogget</p>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: '13px', color: 'var(--color-foreground)' }} title={formatFullTime(session?.loginTime)}>
                    {formatRelativeTime(session?.loginTime)}
                  </p>
                </div>
                <div>
                  <p style={labelStyle}>Enhet</p>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: '13px', color: 'var(--color-foreground)', textTransform: 'capitalize' }}>
                    {session?.deviceType === 'mobile' ? 'Mobil' : 'Datamaskin'}
                  </p>
                </div>
              </div>
            </div>

            {/* Logout button */}
            {!session?.isCurrent && (
              <button
                onClick={() => handleLogout(session?.id)}
                disabled={loggingOutId === session?.id}
                style={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 14px',
                  background: 'transparent',
                  border: '2px solid var(--color-border)',
                  borderRadius: 'var(--radius-sm)',
                  fontFamily: 'var(--font-caption)',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: loggingOutId === session?.id ? 'var(--color-muted-foreground)' : '#dc2626',
                  borderColor: loggingOutId === session?.id ? 'var(--color-border)' : 'rgba(220,38,38,0.4)',
                  cursor: loggingOutId === session?.id ? 'not-allowed' : 'pointer',
                  transition: 'background 150ms, border-color 150ms',
                  alignSelf: 'flex-start',
                }}
                onMouseEnter={e => { if (loggingOutId !== session?.id) { e.currentTarget.style.background = 'rgba(220,38,38,0.08)'; e.currentTarget.style.borderColor = '#dc2626'; }}}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(220,38,38,0.4)'; }}
                aria-label={`Logg ut ${session?.browser} på ${session?.os}`}
              >
                {loggingOutId === session?.id ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                )}
                {loggingOutId === session?.id ? 'Logger ut...' : 'Logg ut'}
              </button>
            )}
          </div>
        ))}

        {activeSessions?.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 24px', color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)', fontSize: '14px' }}>
            Ingen aktive økter funnet
          </div>
        )}
      </div>

      {/* Security tip */}
      <div style={{
        marginTop: '24px',
        padding: '14px 18px',
        background: 'rgba(0,79,152,0.06)',
        border: '1px solid rgba(0,79,152,0.15)',
        borderRadius: 'var(--radius-sm)',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
      }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(0,79,152,0.7)" strokeWidth="2" style={{ flexShrink: 0, marginTop: '1px' }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        <p style={{ fontFamily: 'var(--font-caption)', fontSize: '12px', color: 'var(--color-muted-foreground)', lineHeight: 1.6 }}>
          Gjenkjenner du ikke en enhet? Logg den ut umiddelbart og endre passordet ditt for å sikre kontoen din.
        </p>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes slideUp { from { transform: translateX(-50%) translateY(20px); opacity: 0; } to { transform: translateX(-50%) translateY(0); opacity: 1; } }
      `}</style>
    </div>
  );
};

export default ConnectedDevices;
